import { Box, Typography, keyframes } from '@mui/material'
import { ReactComponent as Stopwatch } from 'assets/icons/svg/Stopwatch.svg'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle'
import ErrorIcon from '@mui/icons-material/Error'
import AddIcon from '@mui/icons-material/Add'
import Cancel from '@mui/icons-material/Cancel'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'

import AdxChip from 'components/reusable/ActionButton/AdxChip'
import { getDayOfDate } from 'services/helper/DateTime'
import { DateRangeOutlined } from '@mui/icons-material'

const AttendanceLabelRenderer = ({ status, withText, withChip, forDaily, isHoliday }) => {
  const styles = {
    container: {
      width: 'fit-content',
      height: 25,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  }

  const getStatusText = (status) => {
    const statusTextMap = {
      1: 'Ongoing',
      2: 'Present',
      3: 'Incomplete',
      4: 'Absent',
      5: 'Late',
      6: 'Underperform',
      7: 'Manual add',
      8: 'On Leave',
      present: 'Present',
      incomplete: 'Incomplete',
      missed_clockout: 'Missed Clock-Out',
      absent: 'Absent',
      late: 'Late',
      late_and_incomplete: 'Late + Incomplete',
      ongoing: 'Ongoing',
      not_clocked_in_yet: 'Not Clocked In',
      non_working_day: 'Off Day',
      worked_on_non_working_day: 'Worked Off Day',
      on_leave: 'On Leave',
    }

    if (isHoliday === true && status !== 'worked_on_non_working_day') return 'Holiday'
    else return statusTextMap[status] || 'Not Available'
  }

  const getIcon = (status) => {
    const iconMap = {
      Ongoing: <Stopwatch sx={{ color: 'alternate.main', ...styles.container }} />,
      1: <Stopwatch sx={{ color: 'alternate.main', ...styles.container }} />,
      Present: <CheckCircleIcon sx={{ color: 'positive.main', ...styles.container }} />,
      2: <CheckCircleIcon sx={{ color: 'positive.main', ...styles.container }} />,
      Incomplete: <RemoveCircleIcon sx={{ color: 'warning.main', ...styles.container }} />,
      3: <RemoveCircleIcon sx={{ color: 'warning.main', ...styles.container }} />,
      Absent: <Cancel sx={{ color: 'error.main', ...styles.container }} />,
      4: <Cancel sx={{ color: 'error.main', ...styles.container }} />,
      Late: <ErrorIcon sx={{ color: 'warning.main', ...styles.container }} />,
      5: <ErrorIcon sx={{ color: 'warning.main', ...styles.container }} />,
      Underperform: <ErrorIcon sx={{ color: 'error.main', ...styles.container }} />,
      6: <ErrorIcon sx={{ color: 'error.main', ...styles.container }} />,
      7: <AddIcon sx={{ color: 'information.main', ...styles.container }} />,
      8: <DateRangeOutlined sx={{ color: 'text.secondary', ...styles.container }} />,
      present: <CheckCircleIcon sx={{ color: 'positive.main', ...styles.container }} />,
      incomplete: <RemoveCircleIcon sx={{ color: 'warning.main', ...styles.container }} />,
      missed_clockout: <ErrorIcon sx={{ color: 'warning.main', ...styles.container }} />,
      absent: <Cancel sx={{ color: 'error.main', ...styles.container }} />,
      late: <ErrorIcon sx={{ color: 'warning.main', ...styles.container }} />,
      late_and_incomplete: <ErrorIcon sx={{ color: 'warning.main', ...styles.container }} />,
      ongoing: <Stopwatch sx={{ color: 'alternate.main', ...styles.container }} />,
      not_clocked_in_yet: (
        <RadioButtonUncheckedIcon sx={{ color: 'text.secondary', ...styles.container }} />
      ),
      non_working_day: <DateRangeOutlined sx={{ color: 'text.secondary', ...styles.container }} />,
      worked_on_non_working_day: <AddIcon sx={{ color: 'information.main', ...styles.container }} />,
      on_leave: <DateRangeOutlined sx={{ color: 'text.secondary', ...styles.container }} />,
    }

    if (isHoliday === true && status !== 'worked_on_non_working_day')
      return <DateRangeOutlined sx={{ color: 'text.secondary', ...styles.container }} />
    else return iconMap[status] || <></>
  }

  const fadeAnimation = keyframes`
    0% { background-color: #3E4E4F; }
    50% { background-color: #182626; }
    100% { background-color: #3E4E4F; }
  `

  return (
    <>
      {withChip ? (
        <AdxChip
          sx={{
            ...(status === 1 && {
              backgroundColor: '#3E4E4F',
              animation: `${fadeAnimation} 5s ease infinite`,
            }),
          }}
          chipBgColor={'gray-background.main'}
          fullWidth
        >
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              justifyContent: 'start',
              whiteSpace: 'nowrap',
            }}
          >
            {getIcon(status)}
            {withText && (
              <Typography
                sx={{
                  fontSize: '14px',
                  color: status === 1 ? 'white.main' : 'dark-text.main',
                }}
              >
                {getStatusText(status)}
              </Typography>
            )}
          </Box>
        </AdxChip>
      ) : (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center' }}>
          {getIcon(status)}
          {withText && <Typography sx={{ fontSize: '14px' }}>{getStatusText(status)}</Typography>}
        </Box>
      )}
    </>
  )
}

export const renderAttendanceType = (type) => {
  const typeMap = {
    1: {
      chipBgColor: 'light-information.main',
      chipColor: 'information.main',
      text: 'Work From Office',
    },
    2: {
      chipBgColor: 'orange.main',
      chipColor: 'orange.contrastText',
      text: 'Work Remotely',
    },
  }

  const { chipBgColor, chipColor, text } = typeMap[type] || {
    chipBgColor: 'gray-background.main',
    chipColor: 'dark-text.main',
    text: '- Not Available -',
  }

  return (
    <AdxChip
      chipBgColor={chipBgColor}
      chipColor={chipColor}
      sx={{
        fontWeight: 500,
        fontSize: '12px',
      }}
    >
      {text}
    </AdxChip>
  )
}

export const renderDay = (date) => {
  const formatDate = (dateString) => {
    const options = { month: 'short', day: '2-digit', year: 'numeric' }
    return new Date(dateString).toLocaleDateString('en-US', options)
  }

  return (
    <>
      {date ? (
        <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              border: 1,
              borderColor: 'gray-line.main',
              borderRadius: '30px',
              width: '50px',
              height: '30px',
            }}
          >
            <Typography sx={{ fontWeight: 600, textWrap: 'nowrap' }}>
              {getDayOfDate(date)}
            </Typography>
          </Box>
          <Typography sx={{ fontWeight: 600, textWrap: 'nowrap' }}>{formatDate(date)}</Typography>
        </Box>
      ) : (
        <Typography>Date not available</Typography>
      )}
    </>
  )
}

export const renderSkills = (skill) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      width: 'fit-content',
      bgcolor: 'gray-background.main',
      p: 1,
      borderRadius: '4.5px',
    }}
  >
    {skill}
  </Box>
)

export const renderCountBadge = (count) => (
  <Box
    sx={{
      width: 'fit-content',
      px: 1,
      bgcolor: count === 0 ? 'grayscale.line-light' : 'alternate.light',
      color: count === 0 ? 'text.secondary' : 'alternate.main',
      borderRadius: '4.5px',
    }}
  >
    <Typography fontWeight={500}>{count}</Typography>
  </Box>
)

export default AttendanceLabelRenderer
